import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Field, reduxForm, change } from 'redux-form';
import { Button, FormControl } from 'react-bootstrap';
import cx from 'classnames';

import DateRange from '../DateRange';
import PlaceGeoSuggest from '../../Common/GeoSuggest/PlaceGeoSuggest';
import MobileDateRange from '../MobileDateRange';

import { setPersonalizedValues } from '../../../actions/personalized';
import detectMobileBrowsers from '../../../helpers/detectMobileBrowsers';
import { searchFormSuggestSelect, changeSearchForm } from '../../../helpers/GeoSuggest/updateSearchForm';
import messages from '../../../locale/jsonMessages';
import history from '../../../core/history';
import { SearchSvgIcon } from '../../Common/Assets/SvgIconComponent/CommonSvgIcon';

import cs from '../../commonStyle.css';
import s from './SearchForm.css';

const SearchForm = (props) => {
  const {
    personalized = {},
    settingsData = { listSettings: [] },
    listingFields,
    setPersonalizedValues,
    change,
    hideLabel,
    intl: { formatMessage },
    id
  } = props;

  const [state, setState] = useState({
    mobileDevice: detectMobileBrowsers.isMobile(),
    isLoad: true,
    personCapacity: [],
    roomType: null,
    smallDevice: false,
    verySmallDevice: false
  });

  const handleResize = useCallback(() => {
    const isBrowser = typeof window !== 'undefined';
    setState(prev => ({
      ...prev,
      smallDevice: isBrowser ? window.matchMedia('(max-width: 767px)').matches : true,
      verySmallDevice: isBrowser ? window.matchMedia('(max-width: 480px)').matches : false
    }));
  }, []);

  useEffect(() => {
    setState(prev => ({ ...prev, isLoad: false }));
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [handleResize]);

  useEffect(() => {
    if (listingFields !== undefined) {
      setState(prev => ({
        ...prev,
        roomType: listingFields.roomType,
        personCapacity: listingFields.personCapacity
      }));
    }
  }, [listingFields]);

  const handleClick = () => {
    let uri = '/s?';
    if (personalized?.chosen != null) {
      uri += `&address=${personalized.location}&chosen=${personalized.chosen}`;
    } else if (personalized?.location != null) {
      uri += `&address=${personalized.location}`;
    }
    if (personalized?.startDate && personalized?.endDate) {
      uri += `&startDate=${personalized.startDate}&endDate=${personalized.endDate}`;
    }
    if (personalized?.personCapacity != null && !isNaN(personalized.personCapacity)) {
      uri += `&guests=${personalized.personCapacity}`;
    }
    history.push(encodeURI(uri));
  };

  const onSuggestSelect = (data, value) => {
    searchFormSuggestSelect({ data, setPersonalizedValues, value });
  };

  const onChangeSearch = (value) => {
    if (value === '') {
      changeSearchForm({ setPersonalizedValues, change });
    }
  };

  const renderFormControl = ({ input, label, type, meta: { touched, error }, className }) => (
    <div>
      {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
      <FormControl {...input} placeholder={label} type={type} className={className} autoComplete="off" />
    </div>
  );

  const renderPlacesSuggest = ({ input, label, meta: { touched, error }, containerClassName }) => (
    <PlaceGeoSuggest
      {...input}
      initialValue={personalized?.location}
      containerClassName={containerClassName}
      placeholder={label}
      onSuggestSelect={onSuggestSelect}
      onChange={onChangeSearch}
      id={'homeSearch' + id}
    />
  );

  const isBrowser = typeof window !== 'undefined';
  const smallDevice = isBrowser ? window.matchMedia('(max-width: 640px)').matches : undefined;

  return (
    <div className={cx(s.homeLayoutSearchGrid, 'homeSearchForm')}>
      <div className={cx(s.rightBorder, 'rightBorderRTL')}>
        {!hideLabel && (
          <label className={cx(cs.commonContentText, s.textTop)}>
            <FormattedMessage {...messages.where} />
          </label>
        )}
        <form autoComplete="off">
          <Field
            component={renderPlacesSuggest}
            autoComplete="off"
            label={formatMessage(messages.homeWhere)}
            containerClassName="layoutTwoGeoSuggest"
          />
        </form>
      </div>
      <div>
        {!hideLabel && (
          <label className={cs.commonContentText}>
            <FormattedMessage {...messages.when} />
          </label>
        )}
        <span className={cx('homeDate', s.formControlInput, s.input)}>
          {!smallDevice ? (
            <DateRange formName="SearchForm" numberOfMonths={2} />
          ) : (
            <MobileDateRange formName="SearchForm" numberOfMonths={1} verySmallDevice={state?.verySmallDevice} />
          )}
        </span>
      </div>
      <div>
        <Button className={cx(cs.btnPrimary, s.btnBlock, s.searchButton)} onClick={handleClick}>
          <SearchSvgIcon className={s.iconFontSize} />
          <span className="searchTextLeft">
            <FormattedMessage {...messages.search} />
          </span>
        </Button>
      </div>
    </div>
  );
};

SearchForm.propTypes = {
  setPersonalizedValues: PropTypes.any.isRequired,
  getSpecificSettings: PropTypes.any,
  personalized: PropTypes.object,
  settingsData: PropTypes.shape({
    listSettings: PropTypes.array.isRequired
  }),
  listingFields: PropTypes.object,
  hideLabel: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  change: PropTypes.func.isRequired,
  id: PropTypes.any
};

export default reduxForm({
  form: 'SearchForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
})(
  injectIntl(
    withStyles(cs, s)(
      connect(
        (state) => ({
          personalized: state?.personalized,
          settingsData: state?.viewListing?.settingsData,
          listingFields: state?.listingFields?.data
        }),
        { setPersonalizedValues, change }
      )(SearchForm)
    )
  )
);
